import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "sonner";
import {
  Cloud, RefreshCw, CheckCircle, XCircle, Settings, Activity,
  Send, Lock, LogOut, Key,
} from "lucide-react";
import { API, LOJACK_LOGO } from "../config";
import { PasswordInput } from "../components/shared/PasswordInput";
import { TelcaVoIPBadge } from "../components/shared/TelcaVoIPBadge";
import { SalesforceLivePulseWidget } from "../components/salesforce/SalesforceLivePulseWidget";

const SalesforcePortalPage = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [userName, setUserName] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [token, setToken] = useState("");

  // Salesforce state
  const [status, setStatus] = useState(null);
  const [settingsMeta, setSettingsMeta] = useState(null);
  const [logs, setLogs] = useState([]);
  const [expandedLogIndex, setExpandedLogIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("settings");

  // Settings form
  const [form, setForm] = useState({
    login_url: "https://login.salesforce.com",
    consumer_key: "",
    consumer_secret: "",
    username: "",
    password: "",
    security_token: "",
    api_version: "v60.0",
    object_type: "Lead",
    enabled: true,
    auto_sync_on_signature: false,
  });

  // Password change
  const [showPwChange, setShowPwChange] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", newPw: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);

  // Check existing session
  useEffect(() => {
    const savedToken = localStorage.getItem("sf_portal_token");
    const savedName = localStorage.getItem("sf_portal_name");
    if (savedToken) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${savedToken}` } })
        .then(res => {
          if (res.data.role === "salesforce" || res.data.role === "super_admin" || res.data.role === "admin") {
            setAuthenticated(true);
            setToken(savedToken);
            setUserName(res.data.name || savedName || "");
          } else {
            localStorage.removeItem("sf_portal_token");
            localStorage.removeItem("sf_portal_name");
          }
        })
        .catch(() => {
          localStorage.removeItem("sf_portal_token");
          localStorage.removeItem("sf_portal_name");
        });
    }
  }, []);

  // Fetch SF data when authenticated
  useEffect(() => {
    if (authenticated && token) fetchAll();
  }, [authenticated, token]);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email: loginEmail, password: loginPassword });
      const role = res.data.user?.role;
      if (role !== "salesforce" && role !== "super_admin" && role !== "admin") {
        setLoginError("Accesso riservato agli utenti autorizzati Salesforce.");
        setLoginLoading(false);
        return;
      }
      localStorage.setItem("sf_portal_token", res.data.token);
      localStorage.setItem("sf_portal_name", res.data.user?.name || "");
      setToken(res.data.token);
      setUserName(res.data.user?.name || "");
      setAuthenticated(true);
    } catch {
      setLoginError("Credenziali non valide.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("sf_portal_token");
    localStorage.removeItem("sf_portal_name");
    setAuthenticated(false);
    setToken("");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPw !== pwForm.confirm) { toast.error("Le password non coincidono"); return; }
    if (pwForm.newPw.length < 6) { toast.error("Minimo 6 caratteri"); return; }
    setPwLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: pwForm.current,
        new_password: pwForm.newPw,
      }, authHeaders());
      toast.success("Password aggiornata");
      setShowPwChange(false);
      setPwForm({ current: "", newPw: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel cambio password");
    } finally {
      setPwLoading(false);
    }
  };

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [statusRes, settingsRes, logsRes] = await Promise.all([
        axios.get(`${API}/salesforce/status`, authHeaders()),
        axios.get(`${API}/salesforce/settings`, authHeaders()),
        axios.get(`${API}/salesforce/sync-logs?limit=20`, authHeaders()),
      ]);
      setStatus(statusRes.data);
      setSettingsMeta(settingsRes.data);
      setLogs(logsRes.data.logs || []);
      const s = settingsRes.data;
      setForm(prev => ({
        ...prev,
        login_url: s.login_url || prev.login_url,
        username: s.username || "",
        api_version: s.api_version || "v60.0",
        object_type: s.object_type || "Lead",
        enabled: s.enabled ?? true,
        auto_sync_on_signature: s.auto_sync_on_signature ?? false,
      }));
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/salesforce/test-connection`, {}, authHeaders());
      toast.success(`Connesso: ${res.data.instance_url}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connessione fallita");
    } finally {
      setTesting(false);
    }
  };

  const saveSettings = async () => {
    // Fabrizio CR 30/09/2026 — the ``/settings`` GET returns masked
    // sensitive fields, so ``form.consumer_key`` etc. are empty right
    // after a page load even when the DB has valid credentials. We
    // only enforce the "obbligatori" check when the DB doesn't yet
    // have anything, otherwise the user gets blocked from just
    // flipping the auto-sync toggle. The backend now does partial
    // updates: empty sensitive fields are preserved on write.
    const dbHasKey = (settingsMeta?.consumer_key_length || 0) > 0;
    const dbHasUsername = !!(settingsMeta?.username);
    if ((!form.consumer_key && !dbHasKey) || (!form.username && !dbHasUsername)) {
      toast.error("Consumer Key e Username obbligatori");
      return;
    }
    // Strip empty sensitive fields from the payload so the backend
    // preserves what's already in DB instead of overwriting to empty.
    const payload = { ...form };
    for (const k of ["consumer_key", "consumer_secret", "password", "security_token"]) {
      if (!payload[k]) delete payload[k];
    }
    if (!payload.username) delete payload.username;
    setSaving(true);
    try {
      await axios.put(`${API}/salesforce/settings`, payload, authHeaders());
      toast.success("Configurazione salvata");
      fetchAll();
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  // Bulk-sync trigger for the Live Pulse CTA. Parent-scoped so the
  // widget stays presentational + the button doesn't need to know
  // the bearer token.
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // { status, processed, total, created, duplicates, failed }

  // Poll ``/customers-sync-summary`` every 2 seconds whenever a bulk
  // job is running so the widget's progress bar reflects the live
  // state (created / duplicates / failed / processed).
  useEffect(() => {
    if (!authenticated || !token) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/salesforce/customers-sync-summary`, authHeaders());
        const job = r.data?.last_job;
        if (cancelled) return;
        if (job && job.status === "running") {
          setBulkProgress({
            status: "running",
            processed: job.processed || 0,
            total: job.total || 0,
            created: job.created || 0,
            duplicates: job.duplicates || 0,
            failed: job.failed || 0,
          });
        } else {
          // Completed → clear the progress overlay and refresh stats
          // one last time so the sparkline / recent-syncs list catch
          // up with the fresh writes.
          setBulkProgress((prev) => {
            if (prev?.status === "running") fetchAll();
            return null;
          });
        }
      } catch {
        // silent — polling should never spam the user with toasts
      }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, token]);

  const handleBulkSync = async () => {
    setBulkSyncing(true);
    try {
      const r = await axios.post(`${API}/salesforce/sync-all-customers`, {}, authHeaders());
      const total = r.data?.total ?? 0;
      if (total === 0) {
        toast.success("Nessun cliente da sincronizzare");
      } else {
        toast.success(`Sync massiva avviata per ${total} clienti`);
        setBulkProgress({
          status: "running",
          processed: 0,
          total,
          created: 0,
          duplicates: 0,
          failed: 0,
        });
      }
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'avvio della sync massiva");
    } finally {
      setBulkSyncing(false);
    }
  };

  // === LOGIN SCREEN ===
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-lojack-black flex items-center justify-center p-4">
        <Toaster position="top-right" toastOptions={{ style: { background: '#1A1A1A', color: '#fff', border: '1px solid #2A2A2A' } }} />
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-4 mb-6">
              <img src={LOJACK_LOGO} alt="LoJack Tracker" className="h-14" />
            </div>
            <h1 className="text-2xl font-bold text-white flex items-center justify-center gap-2">
              <Cloud className="text-blue-400" size={24} />
              Salesforce Integration
            </h1>
            <p className="text-gray-400 mt-2">Configurazione CRM</p>
          </div>

          <div className="wallet-card">
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Email</label>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                  placeholder="email@lojack.it"
                  required
                  data-testid="sf-login-email"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Password</label>
                <PasswordInput
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 pr-12 text-white focus:border-blue-500 focus:outline-none"
                  placeholder="********"
                  required
                  data-testid="sf-login-password"
                />
              </div>
              {loginError && (
                <p className="text-red-400 text-sm text-center" data-testid="sf-login-error">{loginError}</p>
              )}
              <button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
                data-testid="sf-login-submit"
              >
                {loginLoading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <><Lock size={18} />Accedi</>}
              </button>
            </form>
          </div>

          <div className="text-center mt-8">
            <TelcaVoIPBadge />
          </div>
        </div>
      </div>
    );
  }

  // === AUTHENTICATED: SALESFORCE PANEL ===
  const configured = status?.configured;
  const stats = status?.stats || {};

  return (
    <div className="min-h-screen bg-lojack-black">
      <Toaster position="top-right" toastOptions={{ style: { background: '#1A1A1A', color: '#fff', border: '1px solid #2A2A2A' } }} />

      {/* Header */}
      <nav className="bg-lojack-dark border-b border-gray-800 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOJACK_LOGO} alt="LoJack Tracker" className="h-8" />
            <span className="text-gray-600 mx-2">|</span>
            <Cloud className="text-blue-400" size={18} />
            <span className="text-white font-medium text-sm">Salesforce CRM</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-gray-400 text-sm">{userName}</span>
            <button
              onClick={() => setShowPwChange(true)}
              className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
              title="Cambia Password"
              data-testid="sf-change-pw-btn"
            >
              <Key size={16} />
            </button>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-400 hover:text-red-400 transition-colors"
              title="Logout"
              data-testid="sf-logout-btn"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </nav>

      {/* Change Password Modal */}
      {showPwChange && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowPwChange(false)}>
          <div className="bg-[#1A1A1A] border border-gray-700 rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Key className="text-blue-400" size={18} />Cambia Password</h3>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="text-gray-400 text-xs">Password Attuale</label>
                <PasswordInput
                  value={pwForm.current}
                  onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))}
                  className="w-full mt-1 px-4 py-2.5 pr-12 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm"
                  required
                  data-testid="sf-pw-current"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs">Nuova Password</label>
                <PasswordInput
                  value={pwForm.newPw}
                  onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))}
                  className="w-full mt-1 px-4 py-2.5 pr-12 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm"
                  required
                  data-testid="sf-pw-new"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs">Conferma Nuova Password</label>
                <PasswordInput
                  value={pwForm.confirm}
                  onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))}
                  className="w-full mt-1 px-4 py-2.5 pr-12 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm"
                  required
                  data-testid="sf-pw-confirm"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowPwChange(false)} className="flex-1 py-2.5 border border-gray-600 rounded-xl text-gray-400 hover:bg-gray-800 transition-colors text-sm">Annulla</button>
                <button type="submit" disabled={pwLoading} className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors text-sm flex items-center justify-center gap-2" data-testid="sf-pw-submit">
                  {pwLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Salva"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-5xl mx-auto p-6">
        {/* Live Pulse — Motta's daily-driver KPI strip. */}
        <SalesforceLivePulseWidget
          stats={stats}
          configured={configured}
          onBulkSync={handleBulkSync}
          bulkSyncing={bulkSyncing}
          bulkProgress={bulkProgress}
        />

        {/* Status Banner */}
        <div className={`wallet-card mb-6 border ${configured ? 'border-lojack-blue-600/30' : 'border-lojack-clause-600/30'}`} data-testid="sf-portal-status">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${configured ? 'bg-lojack-blue-500/10' : 'bg-lojack-clause-500/10'}`}>
              {configured ? <CheckCircle className="text-lojack-blue-500" size={24} /> : <XCircle className="text-lojack-clause-500" size={24} />}
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold">{configured ? 'Salesforce Configurato' : 'Salesforce Non Configurato'}</h3>
              <p className="text-gray-400 text-sm">
                {configured
                  ? `Oggetto: ${status?.object_type} | Sync: ${stats.successful || 0} completate, ${stats.failed || 0} fallite`
                  : 'Inserisci le credenziali della Connected App per attivare la sincronizzazione'}
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={fetchAll} className="btn-lojack-outline flex items-center gap-2 text-sm" data-testid="sf-portal-refresh">
                <RefreshCw size={14} />Aggiorna
              </button>
              <button onClick={testConnection} disabled={testing || !configured}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors" data-testid="sf-portal-test">
                {testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send size={14} />}
                {testing ? 'Test...' : 'Test Connessione'}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-800 pb-3">
          {[
            { key: 'settings', label: 'Configurazione', icon: Settings },
            { key: 'logs', label: 'Log Sincronizzazione', icon: Activity },
          ].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === t.key ? 'bg-blue-600/20 text-blue-400 border border-blue-600/30' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`} data-testid={`sf-portal-tab-${t.key}`}>
              <t.icon size={14} />{t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[300px]"><div className="spinner"></div></div>
        ) : (
          <>
            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="wallet-card">
                {/* Warning banner — visible when Consumer Key / Secret in DB
                    look like placeholders left over from initial setup.
                    Backend flag ``credentials_look_valid`` is False when
                    consumer_key < 40 chars or consumer_secret < 20 chars. */}
                {settingsMeta && configured && settingsMeta.credentials_look_valid === false && (
                  <div
                    className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4"
                    data-testid="sf-portal-placeholder-warning"
                  >
                    <div className="flex items-start gap-3">
                      <XCircle className="text-amber-300 shrink-0 mt-0.5" size={18} />
                      <div className="flex-1 text-sm text-amber-100 leading-relaxed">
                        <p className="font-semibold text-amber-200 mb-1">Credenziali OAuth incomplete o di test</p>
                        <p className="text-amber-100/90">
                          Le chiavi attualmente salvate sembrano essere placeholder di test
                          (Consumer Key {settingsMeta.consumer_key_length || 0} caratteri, Consumer Secret
                          {" "}{settingsMeta.consumer_secret_length || 0} caratteri). Una vera Connected App Salesforce
                          restituisce un Consumer Key da ~85 caratteri e un Consumer Secret da 32+ caratteri.
                        </p>
                        <p className="text-amber-100/80 mt-2 text-xs">
                          Vai su Salesforce → Setup → App Manager → tua Connected App → <em>View</em>,
                          copia i valori reali di <strong>Consumer Key</strong> e <strong>Consumer Secret</strong>,
                          incollali qui sotto e premi <strong>Salva Configurazione</strong>. Poi <strong>Test Connessione</strong> per validare.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <h3 className="text-white font-semibold mb-6 flex items-center gap-2">
                  <Settings size={16} className="text-blue-400" />Credenziali Connected App
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-gray-400 text-xs">Login URL / My Domain</label>
                    <input
                      type="url"
                      value={form.login_url}
                      onChange={e => setForm(p => ({ ...p, login_url: e.target.value.trim() }))}
                      placeholder="https://lojack.my.salesforce.com"
                      className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm font-mono"
                      data-testid="sfp-login-url"
                    />
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                      <span>Quick fill:</span>
                      <button
                        type="button"
                        onClick={() => setForm(p => ({ ...p, login_url: "https://login.salesforce.com" }))}
                        className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
                        data-testid="sfp-login-url-preset-prod"
                      >
                        Production
                      </button>
                      <span className="text-gray-700">·</span>
                      <button
                        type="button"
                        onClick={() => setForm(p => ({ ...p, login_url: "https://test.salesforce.com" }))}
                        className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
                        data-testid="sfp-login-url-preset-sandbox"
                      >
                        Sandbox
                      </button>
                      <span className="text-gray-600 ml-1">
                        · Per il flow <code className="text-gray-400">client_credentials</code> serve il <strong className="text-gray-300">My Domain</strong> (es. <code className="text-gray-400">https://lojack.my.salesforce.com</code>)
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-400 text-xs">Consumer Key *</label>
                      <input value={form.consumer_key} onChange={e => setForm(p => ({ ...p, consumer_key: e.target.value }))}
                        placeholder="3MVG9..." className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm font-mono" data-testid="sfp-consumer-key" />
                    </div>
                    <div>
                      <label className="text-gray-400 text-xs">Consumer Secret *</label>
                      <PasswordInput value={form.consumer_secret} onChange={e => setForm(p => ({ ...p, consumer_secret: e.target.value }))}
                        placeholder="Inserisci Consumer Secret" className="w-full mt-1 px-4 py-2.5 pr-12 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm font-mono" data-testid="sfp-consumer-secret" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-400 text-xs">Username Salesforce *</label>
                      <input value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
                        placeholder="admin@company.com" className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm" data-testid="sfp-username" />
                    </div>
                    <div>
                      <label className="text-gray-400 text-xs">Password *</label>
                      <PasswordInput value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                        placeholder="Inserisci Password" className="w-full mt-1 px-4 py-2.5 pr-12 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm" data-testid="sfp-password" />
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs">Security Token</label>
                    <PasswordInput value={form.security_token} onChange={e => setForm(p => ({ ...p, security_token: e.target.value }))}
                      placeholder="Token ricevuto via email da Salesforce" className="w-full mt-1 px-4 py-2.5 pr-12 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm font-mono" data-testid="sfp-security-token" />
                    <p className="text-gray-600 text-xs mt-1">Il Security Token viene inviato via email quando crei un utente Salesforce. Va concatenato alla password.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-400 text-xs">API Version</label>
                      <input value={form.api_version} onChange={e => setForm(p => ({ ...p, api_version: e.target.value }))}
                        placeholder="v60.0" className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm" data-testid="sfp-api-version" />
                    </div>
                    <div>
                      <label className="text-gray-400 text-xs">Tipo Oggetto</label>
                      <select value={form.object_type} onChange={e => setForm(p => ({ ...p, object_type: e.target.value }))}
                        className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm" data-testid="sfp-object-type">
                        <option value="Lead">Lead</option>
                        <option value="Contact">Contact</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center justify-between bg-gray-800/50 rounded-xl p-4">
                    <div>
                      <p className="text-white font-medium">Sincronizzazione Automatica alla firma</p>
                      <p className="text-gray-500 text-xs mt-1">Invia automaticamente i dati a Salesforce alla firma del contratto. Se spento, i clienti restano in coda per la sync manuale (pulsante "Sincronizza N" della Live Pulse).</p>
                    </div>
                    <button onClick={() => setForm(p => ({ ...p, auto_sync_on_signature: !p.auto_sync_on_signature }))}
                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ${form.auto_sync_on_signature ? 'bg-lojack-blue-600' : 'bg-gray-700'}`} data-testid="sfp-autosync-toggle">
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${form.auto_sync_on_signature ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button onClick={saveSettings} disabled={saving}
                      className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 text-sm transition-colors" data-testid="sfp-save-btn">
                      {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Settings size={16} />}
                      {saving ? 'Salvataggio...' : 'Salva Configurazione'}
                    </button>
                    <button onClick={testConnection} disabled={testing}
                      className="btn-lojack-outline flex items-center gap-2 text-sm" data-testid="sfp-test-btn">
                      {testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send size={16} />}
                      {testing ? 'Test...' : 'Test Connessione'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Logs Tab */}
            {activeTab === 'logs' && (
              <div className="wallet-card">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Activity size={16} className="text-blue-400" />Storico Sincronizzazioni
                </h3>
                {logs.length > 0 ? (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {logs.map((log, i) => {
                      const isExpanded = expandedLogIndex === i;
                      const isSkipped = log.status === 'skipped';
                      const isFailed = log.status === 'failed' || (log.status !== 'success' && !isSkipped);
                      const badgeText = log.status === 'success' ? 'OK' : (isSkipped ? 'DA COMPLETARE' : 'ERRORE');
                      const badgeColor = log.status === 'success'
                        ? 'bg-lojack-blue-500/10 text-lojack-blue-400'
                        : (isSkipped ? 'bg-amber-500/10 text-amber-300' : 'bg-red-500/10 text-red-400');
                      const iconColor = log.status === 'success'
                        ? 'bg-lojack-blue-500/10 text-lojack-blue-400'
                        : (isSkipped ? 'bg-amber-500/10 text-amber-300' : 'bg-red-500/10 text-red-400');
                      return (
                        <div key={i} className="bg-gray-800/50 rounded-xl overflow-hidden" data-testid={`sfp-log-${i}`}>
                          <button
                            type="button"
                            onClick={() => setExpandedLogIndex(isExpanded ? null : i)}
                            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-800/70 transition-colors text-left"
                            data-testid={`sfp-log-row-toggle-${i}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconColor}`}>
                                {log.status === 'success' ? <CheckCircle size={14} /> : <XCircle size={14} />}
                              </div>
                              <div>
                                <p className="text-white text-sm font-medium">
                                  {log.customer_id?.substring(0, 8)}...
                                  {log.salesforce_record_id && <span className="text-blue-400 text-xs ml-2">SF: {log.salesforce_record_id}</span>}
                                </p>
                                <p className="text-gray-500 text-xs">
                                  {log.started_at && new Date(log.started_at).toLocaleString('it-IT')}
                                  {log.salesforce_object_type && ` | ${log.salesforce_object_type}`}
                                  {log.pdf_attached && ' | PDF allegato'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`text-xs px-2 py-1 rounded-full ${badgeColor}`}>
                                {badgeText}
                              </span>
                              <span className="text-gray-500 text-xs">{isExpanded ? '▲' : '▼'}</span>
                            </div>
                          </button>
                          {isExpanded && (
                            <div className="px-4 pb-4 pt-1 border-t border-gray-700/50 text-xs space-y-2" data-testid={`sfp-log-detail-${i}`}>
                              {isSkipped && (
                                <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                                  <p className="text-amber-300 font-semibold text-[11px] uppercase tracking-wide mb-1">Profilo cliente da completare</p>
                                  <p className="text-amber-100 leading-relaxed">{log.error || 'Campi obbligatori mancanti.'}</p>
                                  {log.missing_fields && log.missing_fields.length > 0 && (
                                    <p className="text-amber-200/80 mt-2">Campi mancanti: <strong className="font-mono">{log.missing_fields.join(', ')}</strong></p>
                                  )}
                                </div>
                              )}
                              {isFailed && log.error && (
                                <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3">
                                  <p className="text-red-300 font-semibold text-[11px] uppercase tracking-wide mb-1">Errore Salesforce</p>
                                  <p className="text-red-100 font-mono break-all whitespace-pre-wrap leading-relaxed" data-testid={`sfp-log-error-${i}`}>{log.error}</p>
                                </div>
                              )}
                              {log.status === 'success' && log.member_number && (
                                <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-3">
                                  <p className="text-green-300 font-semibold text-[11px] uppercase tracking-wide mb-1">Member Number</p>
                                  <p className="text-green-100 font-mono">{log.member_number}</p>
                                </div>
                              )}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-gray-400">
                                <div><span className="text-gray-500">customer_id:</span> <span className="text-gray-200 font-mono break-all">{log.customer_id || '—'}</span></div>
                                {log.customer_email && <div><span className="text-gray-500">email:</span> <span className="text-gray-200 break-all">{log.customer_email}</span></div>}
                                {log.contract_id && <div><span className="text-gray-500">contract_id:</span> <span className="text-gray-200 font-mono break-all">{log.contract_id}</span></div>}
                                {log.salesforce_object_type && <div><span className="text-gray-500">tipo oggetto:</span> <span className="text-gray-200">{log.salesforce_object_type}</span></div>}
                                {log.sync_status && <div><span className="text-gray-500">sync_status:</span> <span className="text-gray-200">{log.sync_status}</span></div>}
                                {log.started_at && <div><span className="text-gray-500">avviato:</span> <span className="text-gray-200">{new Date(log.started_at).toLocaleString('it-IT')}</span></div>}
                                {log.completed_at && <div><span className="text-gray-500">completato:</span> <span className="text-gray-200">{new Date(log.completed_at).toLocaleString('it-IT')}</span></div>}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Cloud size={40} className="mx-auto mb-3 text-gray-700" />
                    <p className="text-gray-500">Nessuna sincronizzazione ancora eseguita</p>
                    <p className="text-gray-600 text-xs mt-1">I dati verranno inviati automaticamente alla firma dei contratti</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-6">
        <TelcaVoIPBadge />
      </div>
    </div>
  );
};

export default SalesforcePortalPage;
